import { DatePipe } from '@angular/common'
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MatDatepicker } from '@angular/material/datepicker'
import { MatStepper } from '@angular/material/stepper'
import { Address, GlobalMessageType, RoutingService } from '@spartacus/core'
import { UserAccountFacade } from '@spartacus/user/account/root'
import { DateTime } from 'luxon'
import { Subscription } from 'rxjs'
import { finalize, map, switchMap } from 'rxjs/operators'
import { BaseCountry } from '../../../../../models/enum/country.enum'
import { ShipmentType } from '../../../../../models/enum/dispenser.enum'
import { IAgreement } from '../../../../../models/interface/agreement.interface'
import { IShipment } from '../../../../../models/interface/shipment.interface'
import { DispenserAgreementService } from '../../../../../services/agreement/dispenser/dispenser-agreement.service'
import { UserCutoffTime, WeekDay } from '../../../../../services/shared/calendar/calendar.model'
import { CalendarService } from '../../../../../services/shared/calendar/calendar.service'
import { CustomGlobalMessageService } from '../../../../../services/shared/custom-global-message/custom-global-message.service'
import { SharedService } from '../../../../../services/shared/shared.service'
import { CustomTableColumnType } from '../../../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../../../shared/custom-components/custom-table/modals/interface/custom-table.interface'

@Component({
  selector: 'app-dispenser-agreement-place-order',
  templateUrl: './dispenser-agreement-place-order.component.html',
  styleUrls: ['./dispenser-agreement-place-order.component.scss'],
  standalone: false
})
export class DispenserAgreementPlaceOrderComponent implements OnInit {
  public agreementCode: string
  public agreement: IAgreement
  public shipment: IShipment
  public shipSelectFormGroup: FormGroup
  public addressSelectFormGroup: FormGroup
  public reviewFormGroup: FormGroup
  public shipCreditNote = false
  public shipEndUserDepot = false
  public shipDistributorDepot = false
  public dispensersQuantityMap: Map<any, any> = new Map<any, any>()
  public shipSelection = false
  public showInitialLoader = true
  public endCustomerAddress: Address
  public distributorAddresses: Map<string, string> = new Map<string, string>()
  public ShipmentTypeEnum = ShipmentType
  @ViewChild('dispenserPlaceOrderStepper') stepper: MatStepper
  public orderPlaced = false
  public totalQuantity = 0
  public finalFormattedAddress: string
  public currentDate: Date
  public showSubmitLoader = false
  public loadingDistributorAddresses = false
  public subscription: Subscription = new Subscription()
  private _disableOnInvalidQuantity = true

  //datepicker cutoff date
  public todayDate: Date
  public minDate: Date
  public holidayDates: string[]
  public monthChanged = false
  private defaultCutoffTime = '12:00:00 PM'
  private hasCutoffTimeRecord = false

  userCutOff: UserCutoffTime
  private cutoffDateTimeInMelbourne: string
  private readonly timezoneToIANAtimezone = new Map<string, string>([
    ['AUSACT', 'Australia/Sydney'],
    ['AUSNSW', 'Australia/Sydney'],
    ['AUSNT', 'Australia/Darwin'],
    ['AUSQLD', 'Australia/Brisbane'],
    ['AUSSA', 'Australia/Adelaide'],
    ['AUSTAS', 'Australia/Hobart'],
    ['AUSVIC', 'Australia/Melbourne'],
    ['AUSWA', 'Australia/Perth'],
    ['NZCHA', 'Pacific/Chatham'],
    ['NZST', 'Pacific/Auckland']
  ])

  public headers: ICustomTableHeader[] = [
    { title: 'dispenserAgreement.materialNumber' },
    { title: 'dispenserAgreement.materialDescription' },
    { title: 'dispenserAgreement.approved' },
    { title: 'dispenserAgreement.dispatched' },
    { title: 'dispenserAgreement.remaining' },
    { title: 'dispenserAgreement.dispenserSelection', type: CustomTableColumnType.INPUT }
  ]

  public columns: ICustomTableColumn[] = [
    { key: 'product.code' },
    { key: 'product.name' },
    { key: 'quantity' },
    { key: 'issuedDispensers' },
    { key: 'remainingDispensers' },
    {
      key: 'dispenserQuantity',
      type: CustomTableColumnType.INPUT,
      input: { type: 'text', translate: true }
    }
  ]

  constructor(
    public dispenserAgreementService: DispenserAgreementService,
    public globalMessageService: CustomGlobalMessageService,
    public sharedService: SharedService,
    private changeDetectorRef: ChangeDetectorRef,
    public routingService: RoutingService,
    private formBuilder: FormBuilder,
    public datePipe: DatePipe,
    public userService: UserAccountFacade,
    private calendarService: CalendarService
  ) {}

  ngOnInit(): void {
    this.sharedService
      .getUrlParam('code')
      .pipe(
        switchMap((code) => {
          this.agreementCode = code
          return this.dispenserAgreementService.getAgreementDetail(code, true)
        })
      )
      .subscribe((detail) => {
        this.showInitialLoader = false
        this.agreement = detail
        this.changeDetectorRef.detectChanges()
      })

    this.shipSelectFormGroup = this.formBuilder.group({
      shipToSelect: ['', Validators.required]
    })
    this.addressSelectFormGroup = this.formBuilder.group({
      requestedDeliveryDate: [''],
      selectedDistributorAddress: [null, Validators.required],
      sameShippingAddress: [''],
      addressLine1: ['', Validators.required],
      addressLine2: ['', Validators.required],
      state: ['', Validators.required],
      postcode: ['', Validators.required],
      deliveryInstructions: [''],
      poNumber: ['']
    })
    this.reviewFormGroup = this.formBuilder.group({
      agreementType: ['', Validators.required],
      serviceProduct: ['', Validators.required]
    })

    this.convertCutOffTimeToMelbourneTimezone()
    this.selectedDeliveryAddressState()
    this.setHolidays()
  }

  // #region set cutOff datetime

  private convertCutOffTimeToMelbourneTimezone(): void {
    const userCutoffTime$ = this.calendarService.getCutOffTime().pipe(
      map((cutoffTime) => {
        //If object empty, the user has no record in cut off time table
        this.hasCutoffTimeRecord = Object.keys(cutoffTime).length > 0
        this.userCutOff = {
          additionaDays: isNaN(Number(cutoffTime?.additionaDays)) ? 0 : Number(cutoffTime?.additionaDays),
          beforeAdditionalDays: isNaN(Number(cutoffTime?.beforeAdditionalDays))
            ? 0
            : Number(cutoffTime?.beforeAdditionalDays),
          cutOffTime: cutoffTime?.cutOffTime ?? this.defaultCutoffTime, // Default to Melbourne cutoff time (12:00:00) if no cutoff is provided
          timeZone: cutoffTime?.timeZone ?? this.defaultTimeZoneForCurrentUser, // Use the user's time zone if provided; otherwise, use the default time zone for the user's country.
          allowedDeliveryWeekDays: cutoffTime.weekDays ?? null
        }

        // Use the user's time zone if found; otherwise, use the default time zone for the user's country.
        const sourceTimeZone =
          this.timezoneToIANAtimezone.get(this.userCutOff.timeZone) ?? this.defaultTimeZoneForCurrentUser
        const targetTimeZone = this.timezoneToIANAtimezone.get('AUSVIC')
        const today = DateTime.now().toFormat('yyyy-MM-dd')
        const dateTimeString = `${today} ${this.userCutOff.cutOffTime}`

        const formattedMelbourne = this.convertToFormattedTimeZone(dateTimeString, sourceTimeZone, targetTimeZone)
        return formattedMelbourne
      })
    )

    this.subscription.add(
      userCutoffTime$.subscribe((res: string) => {
        this.cutoffDateTimeInMelbourne = res
      })
    )
  }

  private get defaultTimeZoneForCurrentUser(): string {
    return this.isAUCountry ? this.timezoneToIANAtimezone.get('AUSVIC') : this.timezoneToIANAtimezone.get('NZST')
  }

  /**
   * Converts a time string from one time zone to another and returns a formatted string.
   * @param dateTimeString - Date and time string (e.g. '2025-10-17 11:00:00 AM')
   * @param sourceTimeZone - IANA time zone of the input time (e.g. 'Pacific/Chatham')
   * @param targetTimeZone - IANA time zone to convert to (e.g. 'Australia/Melbourne')
   * @returns Formatted date-time string in target time zone (e.g. '2025-10-17 08:15:00+11:00')
   */
  private convertToFormattedTimeZone(dateTimeString: string, sourceTimeZone: string, targetTimeZone: string): string {
    const sourceDateTime = DateTime.fromFormat(dateTimeString, 'yyyy-MM-dd hh:mm:ss a', {
      zone: sourceTimeZone
    })

    // Convert to target time zone - 'Australia/Melbourne'
    const targetDateTime = sourceDateTime.setZone(targetTimeZone)

    // Format the result
    return targetDateTime.toFormat('yyyy-MM-dd HH:mm:ssZZ')
  }

  private selectedDeliveryAddressState() {
    const deliveryAddress = this.addressSelectFormGroup
      .get('selectedDistributorAddress')
      .valueChanges.subscribe((value) => {
        this.addressSelectFormGroup.get('requestedDeliveryDate').setValue('')
      })

    this.subscription.add(deliveryAddress)
  }

  public openDatePicker(datepicker: MatDatepicker<any>): void {
    if (this.addressSelectFormGroup.get('requestedDeliveryDate').enabled) {
      this.todayDate = new Date()
      this.minDate = this.calculateMinDate(this.todayDate, this.minDaysCutOff)
      datepicker.open()
    }
  }

  // Calculate the minimum date excluding weekedends and Australian Holidays
  private calculateMinDate(date: Date, minDaysCutOff: number): Date {
    const minDate = new Date(date)
    let count = 0

    while (count < minDaysCutOff) {
      minDate.setDate(minDate.getDate() + 1)
      // Check if the new date is a weekday
      if (minDate.getDay() !== 0 && minDate.getDay() !== 6 && !this.isHoliday(minDate)) {
        count++
      }
    }

    return minDate
  }

  /**
   * Check if current datetime in Melbourne is bigger than cutoff datetime in Melbourne timezone
   */
  private get isCurrentDateTimeBiggerThanCutOff(): boolean {
    const currentMelbourneTime = DateTime.now().setZone('Australia/Melbourne')

    const cutoffTime = DateTime.fromFormat(this.cutoffDateTimeInMelbourne, 'yyyy-MM-dd HH:mm:ssZZ', {
      zone: 'Australia/Melbourne'
    })

    return currentMelbourneTime > cutoffTime
  }

  /**
   * current time (in Melbourne) after cutoff time (in Melbourne) and AU country -> add 2 days to delivery min date
   * current time (in Melbourne) after cutoff time (in Melbourne) and NZ country -> add 2 days to delivery min date
   * current time (in Melbourne) before cutoff time (in Melbourne) and AU country -> add 2 days to delivery min date
   * current time (in Melbourne) before cutoff time (in Melbourne) and NZ country -> add 2 days to delivery min date
   *
   * Exception:
   * If there is no cutofftime record found
   * After the default cutofftime, for AU the default day should be 3, and for NZ it should be 2
   * No additonal days added
   */
  private get minDaysCutOff(): number {
    switch (this.isCurrentDateTimeBiggerThanCutOff) {
      case true: {
        if (!this.hasCutoffTimeRecord) {
          return this.isAUCountry ? 3 : 2
        } else {
          return this.isAUCountry ? 2 + this.userCutOff.additionaDays : 2 + this.userCutOff.additionaDays
        }
      }
      case false:
        return this.isAUCountry ? 2 + this.userCutOff.beforeAdditionalDays : 2 + this.userCutOff.beforeAdditionalDays
    }
  }

  isAllowedDeliveryWeekDay(date: Date): boolean {
    if (!this.userCutOff.allowedDeliveryWeekDays) {
      return true
    } else {
      const todayWeekDay = this.getWeekday(date.getDay())
      return this.userCutOff.allowedDeliveryWeekDays.includes(todayWeekDay)
    }
  }

  private getWeekday(dayNumber: WeekDay): string {
    return WeekDay[dayNumber]
  }

  private disableDate(isHoliday, isWeekend, isAllowedDeliveryWeekDay): boolean {
    if (isHoliday || isWeekend || !isAllowedDeliveryWeekDay) {
      return false
    }

    return true
  }

  isHoliday(date: Date): boolean {
    return this.holidayDates
      ? !!this.holidayDates.find((holidayDate) => holidayDate === this.datePipe.transform(date, 'dd-MM-yyyy'))
      : false
  }

  holidaysAndDeliverydaysFilter = (datepickerDate: Date): boolean => {
    const date = datepickerDate
    if (date) {
      // month changed in calender manually
      if (this.todayDate.getMonth() != date.getMonth()) {
        this.monthChanged = true
      } else if (this.monthChanged && this.todayDate.getMonth() == date.getMonth()) {
        this.monthChanged = false
      }

      // previous dates
      if (this.todayDate > date) {
        return false
      }

      let isWeekend = false // weekend
      const isHoliday = this.isHoliday(date) //holidays
      const isAllowedDeliveryWeekDay = this.isAllowedDeliveryWeekDay(date) //delivery days
      const day = date.getDay()

      // weekend
      if (day == 0 || day == 6) {
        isWeekend = true
      }

      if (this.isAUCountry) {
        return this.disableDate(isHoliday, isWeekend, isAllowedDeliveryWeekDay)
      }
      if (isHoliday || isWeekend || !isAllowedDeliveryWeekDay) {
        return false
      }
    }
    return true
  }

  private get isAUCountry(): boolean {
    return this.sharedService.baseSiteId == BaseCountry.AU
  }
  //#endregion set cutoff datetime

  selectCreditNote(): void {
    if (this.shipCreditNote) {
      return
    }
    this.shipCreditNote = true
    this.shipEndUserDepot = false
    this.shipDistributorDepot = false
    this.shipSelection = true
    this.shipSelectFormGroup.value.shipToSelect = 'CREDIT_NOTE'
    this.addressSelectFormGroup.reset()

    this.headers = [...this.headers]
    this.headers.push({ title: 'dispenserAgreement.invoiceNumber', type: CustomTableColumnType.INPUT })

    this.columns = [...this.columns]
    this.columns.push({ key: 'invoiceNumber', type: CustomTableColumnType.INPUT_INLINE_EDIT })

    this.changeDetectorRef.detectChanges()
  }

  selectEndUserDepot(): void {
    if (this.shipEndUserDepot) {
      return
    }
    this.addressSelectFormGroup.reset()
    this.shipCreditNote = false
    this.shipEndUserDepot = true
    this.shipDistributorDepot = false
    this.shipSelection = true
    this.shipSelectFormGroup.value.shipToSelect = 'SHIP_TO_CUSTOMER'

    this.getEndCustomerAddress()
    this.addressSelectFormGroup.controls.sameShippingAddress.setValue('yes')

    this.removeInvoiceNumberColumn()
  }

  selectDistributorDepot(): void {
    if (this.shipDistributorDepot) {
      return
    }
    this.addressSelectFormGroup.reset()
    this.shipCreditNote = false
    this.shipEndUserDepot = false
    this.shipDistributorDepot = true
    this.shipSelection = true
    this.shipSelectFormGroup.value.shipToSelect = 'SHIP_TO_DISTRIBUTOR'
    this.getDistributorAddresses()

    this.removeInvoiceNumberColumn()
  }

  public get disableOnInvalidQuantity(): boolean {
    return this._disableOnInvalidQuantity
  }

  changeDispenserQuantity(dispenser: any): void {
    const obj = {
      dispenserQuantity: Number(dispenser.dispenserQuantity),
      invoiceNumber: Number(dispenser.invoiceNumber)
    }

    if (dispenser.dispenserQuantity > 0 || dispenser.dispenserQuantity < 0) {
      this.dispensersQuantityMap.set(dispenser.row, obj)
    } else {
      this.dispensersQuantityMap.delete(dispenser.row)
    }

    //on each user input set next button state (enabled/disabled)
    this.setDisableOnInvalidQuantity()
  }

  private setDisableOnInvalidQuantity(): void {
    if (this.dispensersQuantityMap.size <= 0) {
      this._disableOnInvalidQuantity = true
    } else {
      let errorFound = false
      for (const [dispenser, obj] of this.dispensersQuantityMap) {
        if (obj?.dispenserQuantity < 0) {
          this.globalMessageService.add({ key: 'shared.error.negativeQuantity' }, GlobalMessageType.MSG_TYPE_ERROR)
          errorFound = true
        }

        if (obj?.dispenserQuantity > dispenser.quantity || obj?.dispenserQuantity > dispenser.remainingDispensers) {
          this.globalMessageService.add(
            { key: 'shared.error.quantityMoreThanRemaining' },
            GlobalMessageType.MSG_TYPE_ERROR
          )
          errorFound = true
        }
      }

      if (this.shipDistributorDepot && !this.loadingDistributorAddresses && !this.distributorAddresses.size) {
        errorFound = true
        this.globalMessageService.add({ key: 'shared.error.noDistributorAddresses' }, GlobalMessageType.MSG_TYPE_ERROR)
      }

      if (!errorFound) {
        this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR)
      }
      this._disableOnInvalidQuantity = errorFound
    }
  }

  public get disableWhenNoDistributorAddress(): boolean {
    return !this.distributorAddresses?.size || this.loadingDistributorAddresses
  }

  getEndCustomerAddress(): void {
    this.dispenserAgreementService
      .getEndCustomerAddress(this.agreement?.orderCode)
      .pipe(
        finalize(() => {
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe((address) => {
        if (address) {
          this.endCustomerAddress = address
        } else {
          this.addressSelectFormGroup.controls.sameShippingAddress.setValue('no')
          this.endCustomerAddress = null
        }
      })
  }

  getDistributorAddresses(): void {
    this.loadingDistributorAddresses = true
    this.changeDetectorRef.detectChanges()

    this.dispenserAgreementService
      .getDistributorAddresses(this.agreement?.orderCode)
      .pipe(
        finalize(() => {
          this.loadingDistributorAddresses = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe((addresses) => {
        if (addresses && addresses.length) {
          for (const address of addresses) {
            this.distributorAddresses.set(address.id, address)
          }
        } else {
          this.globalMessageService.add(
            { key: 'shared.error.noDistributorAddresses' },
            GlobalMessageType.MSG_TYPE_ERROR
          )
        }
      })
  }

  get disableAddressSelectionForm(): boolean {
    if (this.shipDistributorDepot) {
      if (
        this.addressSelectFormGroup.value.requestedDeliveryDate &&
        this.addressSelectFormGroup.value.selectedDistributorAddress
      ) {
        return false
      }
    } else if (this.shipEndUserDepot) {
      if (
        this.addressSelectFormGroup.value.sameShippingAddress == 'no' &&
        this.addressSelectFormGroup.value.addressLine1 &&
        this.addressSelectFormGroup.value.addressLine2 &&
        this.addressSelectFormGroup.value.state &&
        this.addressSelectFormGroup.value.postcode &&
        this.addressSelectFormGroup.value.requestedDeliveryDate
      ) {
        return false
      } else if (this.addressSelectFormGroup.value.sameShippingAddress == 'yes') {
        return false
      }
    }
    return true
  }

  public moveToStep(index: number): void {
    this.stepper.selectedIndex = index
  }

  selectDistributorAddress(addressId: any): void {
    this.addressSelectFormGroup.value.selectedDistributorAddress = addressId
  }

  placeDispenserOrder(): void {
    this.showSubmitLoader = true
    const entrires = []
    this.dispensersQuantityMap.forEach((obj: any, entry: any) => {
      entrires.push({
        product: {
          code: entry.product.code
        },
        quantitySelected: obj?.dispenserQuantity,
        unit: 'pieces',
        invoiceNumber: obj?.invoiceNumber
      })
      this.totalQuantity += obj?.dispenserQuantity
    })

    let shipmentAddress: any
    if (this.shipDistributorDepot) {
      // TODO:Spartacus - Method 'get' was removed from Class 'UserService'. Use 'UserAccountFacade.get()' from '@spartacus/user' instead.
      shipmentAddress = this.distributorAddresses.get(this.addressSelectFormGroup.value.selectedDistributorAddress)
      this.finalFormattedAddress = shipmentAddress.formattedAddress
    } else if (this.shipEndUserDepot) {
      if (this.addressSelectFormGroup.value.sameShippingAddress == 'yes') {
        shipmentAddress = this.endCustomerAddress
        this.finalFormattedAddress = this.endCustomerAddress?.formattedAddress
      } else {
        shipmentAddress = {
          line1: this.addressSelectFormGroup.value.addressLine1,
          line2: this.addressSelectFormGroup.value.addressLine2,
          postalCode: this.addressSelectFormGroup.value.postcode,
          town: this.addressSelectFormGroup.value.state
        }
        this.finalFormattedAddress =
          shipmentAddress.line1 + ',' + shipmentAddress.line2 + ' ' + shipmentAddress.postalCode
      }
    }

    const payload = {
      address: shipmentAddress,
      deliveryInstructions: this.addressSelectFormGroup.value.deliveryInstructions,
      poNumber: this.addressSelectFormGroup.value.poNumber,
      shipmentDate: this.datePipe.transform(
        this.addressSelectFormGroup.value.requestedDeliveryDate,
        "yyyy-MM-dd'T'HH:mm:ssZ"
      ),
      entries: entrires,
      quote: {
        orderCode: this.agreement.orderCode
      },
      type: this.shipSelectFormGroup.value.shipToSelect
    }

    this.dispenserAgreementService
      .placeDispenserOrder(payload, this.agreement.orderCode)
      .pipe(
        switchMap((detail) => {
          this.shipment = detail
          return this.dispenserAgreementService.getAgreementDetail(this.agreementCode, true)
        })
      )
      .subscribe(
        (detail) => {
          this.agreement = detail
          this.orderPlaced = true
          this.showSubmitLoader = false
          this.changeDetectorRef.detectChanges()
        },
        (err) => {
          this.showSubmitLoader = false
          this.dispensersQuantityMap.clear()
          this.changeDetectorRef.detectChanges()

          setTimeout(() => {
            this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
          }, 0)
        }
      )
  }

  backToHistory(): void {
    this.resetAll()
  }

  public resetAll(): void {
    this.showInitialLoader = false
    this.shipSelectFormGroup.reset()
    this.addressSelectFormGroup.reset()
    this.reviewFormGroup.reset()
    this.shipCreditNote = false
    this.shipEndUserDepot = false
    this.shipDistributorDepot = false
    this.shipSelection = false
    this.dispensersQuantityMap.clear()
    this.endCustomerAddress = null
    this.distributorAddresses.clear()
    this.orderPlaced = false
    this.totalQuantity = 0
    this.finalFormattedAddress = null
    this.currentDate = null
    this.showSubmitLoader = false

    this.removeInvoiceNumberColumn()
    this.changeDetectorRef.detectChanges()
  }

  private removeInvoiceNumberColumn(): void {
    this.headers = this.headers.filter((item) => {
      return item.title != 'dispenserAgreement.invoiceNumber'
    })
    this.columns = this.columns.filter((item) => {
      return item.key != 'invoiceNumber'
    })
  }

  public setHolidays() {
    this.calendarService.getHolidays().subscribe(
      (dates: string[]) => {
        if (dates && dates.length > 0) {
          this.holidayDates = dates
        }
      },
      (err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      }
    )
  }
}
